// URL state for the /compare route — every URL is a complete recipe for
// reproducing a comparison (AGENTS.md).
//
//   /compare?items=<id1>,<id2>,…&baseline=<id>&view=diff|single&diffMode=structured|gitlab|side-by-side|ask-ai&pair=<id>&hidden=<id>,…
//
// `items` is a comma-separated list of `app:` / `chart:` ids. `baseline`
// must be one of them (defaults to items[0] when missing or invalid).
// `view` toggles the SingleViewer ↔ DiffTabs layout.
// `diffMode` selects which diff renderer tab is active for 2+ items.
// `pair` selects the open pair tab when the set has ≥ 3 items.
// `hidden` lists item ids left out of the values frame. It is omitted
// when every card is included. The baseline is never hidden.
//
// We keep the schema in plain zod plus tiny parse/format helpers so the
// router commit can consume it without coupling this module to a specific
// router implementation. localStorage-backed UI preferences (filters,
// theme) live elsewhere — never in the URL.

import { z } from "zod";
import { isItemId } from "./item-id";

export const VIEW_VALUES = ["diff", "single"] as const;
export type ViewMode = (typeof VIEW_VALUES)[number];
export const DIFF_MODE_VALUES = ["side-by-side", "structured", "gitlab", "ask-ai"] as const;
export type DiffMode = (typeof DIFF_MODE_VALUES)[number];

const itemIdSchema = z
  .string()
  .min(1)
  .refine(isItemId, { message: "not a valid item id" });

/** Validated, normalised search-params shape. */
export const compareSearchSchema = z.object({
  items: z.array(itemIdSchema).default([]),
  baseline: itemIdSchema.optional(),
  view: z.enum(VIEW_VALUES).default("diff"),
  diffMode: z.enum(DIFF_MODE_VALUES).default("side-by-side"),
  pair: itemIdSchema.optional(),
  hidden: z.array(itemIdSchema).default([]),
});

export type CompareSearch = z.infer<typeof compareSearchSchema>;

interface RawSearch {
  items?: string | null;
  baseline?: string | null;
  view?: string | null;
  diffMode?: string | null;
  pair?: string | null;
  hidden?: string | null;
}

/**
 * Parse comma-separated `items` plus optional companions from a raw
 * URLSearchParams-shaped object into a fully validated `CompareSearch`.
 *
 * The parser is lenient: unknown tokens drop out of `items`, an invalid
 * `baseline` falls back to `items[0]`, and a `pair` that no longer
 * matches a non-baseline item is dropped. The router uses the result as
 * a "what we believe the URL means", and the caller writes the
 * normalised form back so the URL self-corrects.
 */
export function parseCompareSearch(input: RawSearch | URLSearchParams): CompareSearch {
  const raw = isUrlSearchParams(input) ? readFromUrlSearchParams(input) : input;

  const rawItems = splitItemIds(raw.items);

  const items = dedupe(rawItems);
  const baseline = raw.baseline && isItemId(raw.baseline) && items.includes(raw.baseline)
    ? raw.baseline
    : items[0];

  const view: ViewMode =
    raw.view === "single" || raw.view === "diff" ? raw.view : "diff";
  const diffMode: DiffMode = isDiffMode(raw.diffMode) ? raw.diffMode : "side-by-side";

  const pair =
    raw.pair && isItemId(raw.pair) && items.includes(raw.pair) && raw.pair !== baseline
      ? raw.pair
      : undefined;

  const hidden = canonicalHidden(splitItemIds(raw.hidden), items, baseline);

  return compareSearchSchema.parse({ items, baseline, view, diffMode, pair, hidden });
}

/**
 * Hidden ids that are members of `items` and are not the baseline,
 * written in comparison-set order.
 */
export function canonicalHidden(
  hidden: readonly string[],
  items: readonly string[],
  baseline: string | undefined,
): string[] {
  const allowed = new Set(
    hidden.filter((id) => isItemId(id) && items.includes(id) && id !== baseline),
  );
  return items.filter((id) => allowed.has(id));
}

/** Comparison-set members that stay in the values frame. */
export function includedItems(
  items: readonly string[],
  hidden: readonly string[],
): string[] {
  const hiddenSet = new Set(hidden);
  return items.filter((id) => !hiddenSet.has(id));
}

/** Serialise a normalised `CompareSearch` back into a `URLSearchParams`. */
export function formatCompareSearch(state: CompareSearch): URLSearchParams {
  const out = new URLSearchParams();
  if (state.items.length > 0) {
    out.set("items", state.items.join(","));
  }
  if (state.baseline) {
    out.set("baseline", state.baseline);
  }
  if (state.view && state.view !== "diff") {
    out.set("view", state.view);
  }
  if (state.diffMode && state.diffMode !== "side-by-side") {
    out.set("diffMode", state.diffMode);
  }
  if (state.pair) {
    out.set("pair", state.pair);
  }
  const hidden = canonicalHidden(state.hidden ?? [], state.items, state.baseline);
  if (hidden.length > 0) {
    out.set("hidden", hidden.join(","));
  }
  return out;
}

/** Convenience: build the full `?items=…` query string. */
export function compareSearchToString(state: CompareSearch): string {
  const params = formatCompareSearch(state);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function isDiffMode(value: string | null | undefined): value is DiffMode {
  return (
    value != null && (DIFF_MODE_VALUES as readonly string[]).includes(value)
  );
}

function splitItemIds(value: string | null | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .filter(isItemId);
}

function dedupe<T>(values: readonly T[]): T[] {
  const seen = new Set<T>();
  const out: T[] = [];
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

function isUrlSearchParams(value: unknown): value is URLSearchParams {
  return typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams;
}

function readFromUrlSearchParams(params: URLSearchParams): RawSearch {
  return {
    items: params.get("items"),
    baseline: params.get("baseline"),
    view: params.get("view"),
    diffMode: params.get("diffMode"),
    pair: params.get("pair"),
    hidden: params.get("hidden"),
  };
}
